import { type FC, type ReactNode } from "react";
import { generatePath } from "react-router-dom";

import { useFragment } from "@apollo/client";

import { useFeatureGates } from "../../../../cof-feature-gates/hooks";
import { AbsoluteRoute } from "../../../../core/routes";
import {
    CofConfigId,
    type LensSpace,
    type MyLensesLens,
    type ResourceContainer,
    ResourceKind,
} from "../../../../gql-generated/types";
import { lensDataContainerStyles } from "../../../../lens/components/shared";
import { lensSpace, resourceContainer } from "../../../../shared/api/fragments";
import { useSearchParams } from "../../../../shared/hooks";
import { LinkStyled } from "../../../../shared/styles";

interface LensLinkWrapperProps {
    children: ReactNode;
    lens: MyLensesLens;
}

export const LensLinkWrapper: FC<LensLinkWrapperProps> = ({ lens, children }) => {
    const lensFolderId = lens.lensAccountId?.lensSpaceId;

    const searchParams = useSearchParams();

    const { data: lensFolderDataV1 } = useFragment<LensSpace>({
        fragment: lensSpace,
        fragmentName: "LensSpaceAll",
        from: {
            __typename: "LensSpace",
            id: lensFolderId,
        },
    });

    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const { data: lensFolderDataV2 } = useFragment<ResourceContainer>({
        fragment: resourceContainer,
        fragmentName: "ResourceContainerAll",
        from: {
            __typename: "ResourceContainer",
            id: lensFolderId,
            kind: ResourceKind.LensSpaces,
        },
    });

    const orgId = isMyLensesOrgsV2Enabled ? lensFolderDataV2.parentName ?? "" : lensFolderDataV1.organizationId ?? "";

    if (lensFolderId) {
        return (
            <LinkStyled
                className={lensDataContainerStyles}
                to={{
                    pathname: generatePath(AbsoluteRoute.ORG_LENS_FOLDER_LENS, {
                        orgId,
                        lensFolderId,
                        lensId: lens.id,
                    }),
                    search: searchParams.toString(),
                }}
            >
                {children}
            </LinkStyled>
        );
    }

    return <>{children}</>;
};
